/**
 * Route call audio to the loudspeaker or the earpiece — as far as the web allows.
 *
 * There is no universal "speakerphone" switch in a browser; there are two
 * partial levers, and this helper pulls whichever exists:
 *
 *   - iOS Safari (16.4+): the Audio Session API. `type = "play-and-record"`
 *     routes to the earpiece like a phone call; `"playback"` routes to the
 *     loudspeaker. This is the ONLY lever iOS gives a web page.
 *   - Chrome (Android + desktop): `HTMLMediaElement.setSinkId`. Output devices
 *     are matched by label ("speaker" / "earpiece"), which only works while we
 *     hold mic permission — always true mid-call. The empty-string sink is the
 *     platform default, which on phones IS the loudspeaker for media.
 *
 * Best-effort by design: returns whether anything was actually applied, and the
 * UI reflects intent either way — on platforms with neither lever (some
 * desktops) the OS routing was already the only routing.
 */
export async function applySpeakerRoute(audioEl, speakerOn) {
    let applied = false;

    try {
        if (typeof navigator !== "undefined" && navigator.audioSession) {
            navigator.audioSession.type = speakerOn
                ? "playback"
                : "play-and-record";
            applied = true;
        }
    } catch {
        /* Audio Session unsupported or rejected — try the sink route */
    }

    try {
        if (
            audioEl &&
            typeof audioEl.setSinkId === "function" &&
            navigator.mediaDevices?.enumerateDevices
        ) {
            const outs = (
                await navigator.mediaDevices.enumerateDevices()
            ).filter((d) => d.kind === "audiooutput");
            const find = (re) =>
                outs.find((d) => re.test(d.label || ""))?.deviceId;

            const target = speakerOn
                ? (find(/speaker/i) ?? "")
                : (find(/earpiece|receiver|handset/i) ??
                  find(/communications/i) ??
                  null);

            if (target !== null) {
                await audioEl.setSinkId(target);
                applied = true;
            }
        }
    } catch {
        /* sink switch refused (no permission / device gone) — keep going */
    }

    return applied;
}

export default applySpeakerRoute;
